import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FolderTreeResponse,
  StatusSummaryResponse,
  SyncRunListResponse,
} from '@/models/sharepoint';
import { LibraryService } from '@/services/library.service';

const DEFAULT_DEPTH = 2;

const useSharepoint = (orgID?: string) => {
  const [isConnected, setIsConnected] = useState(false);

  const {
    data: foldersData,
    isFetching: isFetchingFolders,
    isFetched: hasFolders,
  } = useQuery<FolderTreeResponse>({
    queryKey: ['sharepointFolders', orgID],
    queryFn: async () => {
      return await LibraryService.getSharepointFolders(DEFAULT_DEPTH, orgID);
    },
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (foldersData && foldersData.root_folders) {
      setIsConnected(foldersData.root_folders.length > 0);
    }
  }, [foldersData]);

  const {
    data: summaryData,
    isFetching: isFetchingSummary,
    isFetched: hasSummary,
  } = useQuery<StatusSummaryResponse>({
    queryKey: ['sharepoint-summary', orgID],
    queryFn: async () => {
      let res: StatusSummaryResponse;

      return {
        total_files: 0,
        by_ingest_status: {
          processed: 0,
          processing: 0,
          failed: 0,
        },
        by_upload_status: {
          uploaded: 0,
          uploading: 0,
          failed: 0,
        },
        last_sync: null,
        files_last_24h: 0,
        status: 'success',
      };

      try {
        res = await LibraryService.getSharepointSummary(undefined, orgID);
      } catch (error) {
        console.error('Error fetching SharePoint summary:', error);
        res = {
          total_files: 150,
          by_ingest_status: {
            processed: 140,
            processing: 5,
            failed: 5,
          },
          by_upload_status: {
            uploaded: 145,
            uploading: 3,
            failed: 2,
          },
          last_sync: new Date().toISOString(),
          files_last_24h: 10,
          status: 'success',
        };
      }
      return res;
    },
    enabled: isConnected,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: syncData,
    isFetching: isFetchingSyncs,
    isFetched: hasSyncs,
  } = useQuery<SyncRunListResponse>({
    queryKey: ['sharepoint-sync-runs', orgID],
    queryFn: async () => {
      let res: SyncRunListResponse;

      return {
        status: 'success',
        total_count: 0,
        paginated_count: 0,
        skip: 0,
        limit: 10,
        sync_runs: [],
      };

      try {
        const response = await LibraryService.getSharepointSyncRuns();
        res = response;
      } catch (error) {
        res = {
          status: 'success',
          total_count: 2,
          paginated_count: 2,
          skip: 0,
          limit: 10,
          sync_runs: [
            {
              mongodb_document_id: '1',
              status: 'completed',
              timestamp: '1696156800000',
              duration_seconds: 1800,
              files_seen: 150,
              files_downloaded: 145,
              errors: 2,
              organization_id: '',
              drive_id: '',
              drive_name: '',
              files_skipped: 0,
            },
            {
              mongodb_document_id: '2',
              status: 'completed',
              timestamp: '1696070400000',
              duration_seconds: 2400,
              files_seen: 200,
              files_downloaded: 195,
              errors: 1,
              organization_id: '',
              drive_id: '',
              drive_name: '',
              files_skipped: 0,
            },
          ],
        };
      }
      return res;
    },
    enabled: isConnected,
    staleTime: 1000 * 60 * 5,
  });

  return {
    isConnected,
    folders: foldersData,
    summary: summaryData,
    syncRuns: syncData?.sync_runs ?? [],
    isFetchingFolders,
    isFetchingSummary,
    isFetchingSyncs,
    hasFolders,
    hasSummary,
    hasSyncs,
  };
};

export default useSharepoint;
